import { useState } from "react";
import { ChevronDown, Flame, Download, ArrowUpRight, Sparkles } from "lucide-react";
import { waLink } from "@/lib/brand";
import { BBQ_MENU, BBQ_FEATURED } from "@/lib/bbqMenu";

/**
 * BBQ Assador — premium editorial menu
 * - Light "sand" background to contrast the dark steakhouse theme
 * - "Pratos da Semana" featured block at the top
 * - Desktop: sticky tab rail + editorial grid
 * - Mobile: smooth accordion (first category open)
 * - Discreet PDF download CTA at the bottom
 */
export const BBQMenu = () => {
  const [active, setActive] = useState(BBQ_MENU[0].id);
  const [openId, setOpenId] = useState(BBQ_MENU[0].id);

  const message = "Olá! Gostaria de fazer uma reserva no BBQ Assador.";
  const current = BBQ_MENU.find((c) => c.id === active) || BBQ_MENU[0];

  return (
    <div
      data-testid="bbq-menu"
      className="relative bg-[#F5EDD9] text-[#1A1510]"
    >
      {/* top texture band */}
      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#EADFBE] to-transparent pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
        {/* ============ HEADER ============ */}
        <div className="max-w-3xl">
          <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
            Capítulo 04 — A carta
          </span>
          <h2 className="mt-5 font-serif-bbq text-[#1A1510] text-5xl sm:text-6xl lg:text-7xl tracking-[-0.035em] leading-[1]">
            A nossa
            <br />
            <em className="italic text-[#E85A1F]">mesa.</em>
          </h2>
          <p className="mt-7 font-sans-bbq text-[#6B5B43] text-base sm:text-lg leading-relaxed max-w-xl">
            Petiscos tradicionais, grelhados portugueses, peixe fresco e carnes selecionadas.
            Uma carta pensada para quem gosta de comer bem, sem pressas.
          </p>
        </div>

        {/* ============ PRATOS DA SEMANA ============ */}
        <FeaturedBlock />

        {/* ============ DESKTOP: tabs + grid ============ */}
        <div className="mt-24 hidden md:grid grid-cols-12 gap-12 lg:gap-16">
          <aside className="col-span-4 lg:col-span-3">
            <div className="sticky top-28">
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#E85A1F] font-sans-bbq">
                Categorias
              </span>
              <ul className="mt-6 border-l border-[#1A1510]/10">
                {BBQ_MENU.map((c) => {
                  const isActive = c.id === active;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActive(c.id)}
                        data-testid={`bbq-menu-tab-${c.id}`}
                        className={`group w-full text-left pl-5 pr-3 py-3 -ml-px border-l-2 transition-all duration-300 ${
                          isActive
                            ? "border-[#E85A1F] text-[#1A1510]"
                            : "border-transparent text-[#6B5B43] hover:text-[#1A1510] hover:border-[#1A1510]/30"
                        }`}
                      >
                        <span
                          className={`block font-serif-bbq text-xl leading-tight transition-transform duration-500 ${
                            isActive ? "italic translate-x-0.5" : "group-hover:translate-x-0.5"
                          }`}
                        >
                          {c.label}
                        </span>
                        <span className="mt-0.5 block text-[10px] tracking-[0.28em] uppercase text-[#6B5B43]/70 font-sans-bbq">
                          {c.items.length} itens
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bbq-menu-reserve"
                className="btn btn-md btn-primary-bbq mt-10 w-full"
              >
                Reservar via WhatsApp
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </a>
            </div>
          </aside>

          <section className="col-span-8 lg:col-span-9" key={current.id}>
            <div className="reveal-up is-visible">
              <span className="text-[10px] tracking-[0.45em] uppercase text-[#E85A1F] font-sans-bbq">
                {current.label}
              </span>
              <h3 className="mt-3 font-serif-bbq italic text-[#1A1510] text-4xl lg:text-5xl font-light leading-[1.05] tracking-[-0.02em]">
                {current.intro || current.label}
              </h3>
              <div className="mt-6 h-px w-14 bg-[#E85A1F]" />
            </div>

            <DishGrid items={current.items} />
          </section>
        </div>

        {/* ============ MOBILE: accordion ============ */}
        <div className="mt-16 md:hidden space-y-2" data-testid="bbq-menu-mobile">
          {BBQ_MENU.map((c) => {
            const isOpen = openId === c.id;
            return (
              <div
                key={c.id}
                className="bg-white/55 border border-[#1A1510]/10 rounded-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : c.id)}
                  data-testid={`bbq-accordion-${c.id}`}
                  aria-expanded={isOpen}
                  aria-controls={`bbq-panel-${c.id}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-5 text-left"
                >
                  <div>
                    <span
                      className={`text-[10px] tracking-[0.32em] uppercase ${
                        isOpen ? "text-[#E85A1F]" : "text-[#6B5B43]"
                      } font-sans-bbq transition-colors`}
                    >
                      {c.items.length} itens
                    </span>
                    <h4
                      className={`mt-1 font-serif-bbq ${
                        isOpen ? "italic" : ""
                      } text-[#1A1510] text-2xl leading-tight tracking-tight`}
                    >
                      {c.label}
                    </h4>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-[#1A1510] shrink-0 transition-transform duration-500 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    strokeWidth={1.5}
                  />
                </button>

                <div
                  id={`bbq-panel-${c.id}`}
                  role="region"
                  aria-hidden={!isOpen}
                  className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pb-6">
                      {c.intro && (
                        <p className="font-sans-bbq text-[13px] text-[#6B5B43] leading-relaxed mb-4">
                          {c.intro}
                        </p>
                      )}
                      <DishList items={c.items} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="mt-10 px-7 py-12 bg-[#E8DFD0] rounded-sm text-center">
            <p className="font-serif-bbq italic text-[#1A1510] text-xl mb-5">
              Prefere garantir lugar? Fale connosco.
            </p>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bbq-menu-reserve-mobile"
              className="btn btn-md btn-primary-bbq w-full"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>

        {/* ============ PDF DOWNLOAD ============ */}
        <div className="mt-20 sm:mt-28 flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#1A1510]/10">
          <p className="font-sans-bbq text-xs tracking-[0.3em] uppercase text-[#6B5B43] text-center sm:text-left">
            Preços em Euros · IVA incluído · Sujeitos a alteração
          </p>
          <a
            href="/menu-bbq.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bbq-menu-download"
            className="group inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase font-sans-bbq text-[#1A1510] hover:text-[#E85A1F] transition-colors duration-300"
          >
            <span className="h-9 w-9 rounded-full border border-[#1A1510]/30 group-hover:border-[#E85A1F] flex items-center justify-center transition-colors duration-300">
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
            </span>
            Descarregar menu (PDF)
          </a>
        </div>
      </div>

      {/* bottom fade back to dark */}
      <div className="h-20 bg-gradient-to-b from-transparent to-[#1A0E08]" />
    </div>
  );
};

/* ============ FEATURED — Pratos da Semana ============ */
const TAG_STYLES = {
  popular: {
    label: "Popular",
    cls: "bg-[#E85A1F]/12 text-[#C44713] border-[#E85A1F]/25",
  },
  recomendado: {
    label: "Recomendado",
    cls: "bg-[#D4A849]/15 text-[#8A6A1E] border-[#D4A849]/35",
  },
  especialidade: {
    label: "Especialidade",
    cls: "bg-[#1A1510] text-[#F5EDD9] border-[#1A1510]",
  },
};

const TagPill = ({ tag, size = "sm" }) => {
  const t = TAG_STYLES[tag];
  if (!t) return null;
  const base =
    size === "xs"
      ? "px-2 py-0.5 text-[9px] tracking-[0.24em]"
      : "px-2.5 py-1 text-[10px] tracking-[0.28em]";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border uppercase font-sans-bbq ${base} ${t.cls}`}
    >
      {t.label}
    </span>
  );
};

const FeaturedBlock = () => (
  <div className="mt-16 sm:mt-20 relative">
    <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1A1510] text-[#F5EDD9] text-[10px] tracking-[0.32em] uppercase font-sans-bbq">
          <Flame className="h-3 w-3 text-[#E85A1F]" strokeWidth={2} />
          Pratos da Semana
        </span>
      </div>
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B5B43] font-sans-bbq hidden sm:flex items-center gap-2">
        <Sparkles className="h-3 w-3 text-[#D4A849]" strokeWidth={1.5} />
        Seleção do chef
      </span>
    </div>

    <div className="relative bg-[#FBF4DC] border border-[#1A1510]/10 shadow-[0_30px_60px_-30px_rgba(26,21,16,0.25)]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(232,90,31,0.08),transparent_55%)] pointer-events-none" />
      <div className="relative grid grid-cols-1 md:grid-cols-2">
        {BBQ_FEATURED.map((item, idx) => (
          <div
            key={item.name}
            data-testid={`bbq-featured-${idx}`}
            className={`relative p-6 sm:p-8 lg:p-10 group ${
              idx % 2 === 0 ? "md:border-r" : ""
            } ${idx < 2 ? "md:border-b" : ""} border-[#1A1510]/10`}
          >
            <div className="flex items-start justify-between gap-4">
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#6B5B43] font-sans-bbq">
                {item.category}
              </span>
              <TagPill tag={item.tag} />
            </div>
            <h4 className="mt-4 font-serif-bbq text-[#1A1510] text-2xl sm:text-3xl tracking-[-0.02em] leading-tight transition-transform duration-500 group-hover:translate-x-0.5">
              {item.name}
            </h4>
            {item.note && (
              <p className="mt-2 font-sans-bbq text-sm text-[#6B5B43] leading-relaxed max-w-sm">
                {item.note}
              </p>
            )}
            <div className="mt-6 flex items-end justify-between">
              <span className="font-serif-bbq italic text-[#E85A1F] text-2xl sm:text-3xl tracking-tight">
                {item.price}
                <span className="text-[#E85A1F] ml-0.5">€</span>
              </span>
              <span className="h-px flex-1 ml-6 bg-[#1A1510]/10" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ============ Dishes ============ */
const DishGrid = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-x-12 lg:gap-x-20">
    {items.map((it, idx) => (
      <DishRow key={idx} item={it} />
    ))}
  </div>
);

const DishList = ({ items }) => (
  <ul className="divide-y divide-[#1A1510]/10">
    {items.map((it, idx) => (
      <li key={idx}>
        <DishRow item={it} compact />
      </li>
    ))}
  </ul>
);

const slug = (s) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-");

const DishRow = ({ item, compact = false }) => (
  <div
    className={`group flex items-start justify-between gap-5 ${
      compact ? "py-4" : "py-6 border-b border-[#1A1510]/10"
    } transition-colors duration-300 hover:bg-[#EADFBE]/50 px-2 -mx-2 rounded-sm cursor-default`}
    data-testid={`bbq-dish-${slug(item.name)}`}
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h5
          className={`font-serif-bbq text-[#1A1510] ${
            compact ? "text-lg" : "text-xl lg:text-[1.375rem]"
          } leading-tight tracking-tight transition-all duration-500 group-hover:translate-x-1 group-hover:text-[#C44713] underline-offset-4 decoration-[#E85A1F]/0 group-hover:decoration-[#E85A1F]/70 group-hover:underline decoration-1`}
        >
          {item.name}
        </h5>
        {item.tag && <TagPill tag={item.tag} size="xs" />}
      </div>
      {item.note && (
        <p className="mt-1.5 font-sans-bbq text-[13px] text-[#6B5B43] leading-relaxed">
          {item.note}
        </p>
      )}
    </div>
    <div className="shrink-0 text-right">
      <span
        className={`font-serif-bbq italic text-[#1A1510]/85 group-hover:text-[#E85A1F] ${
          compact ? "text-base" : "text-lg lg:text-xl"
        } tracking-tight transition-colors duration-500`}
      >
        {item.price}
        <span className="text-[#E85A1F] ml-0.5">€</span>
      </span>
    </div>
  </div>
);

export default BBQMenu;
